import React, { useContext, useEffect, useRef, useState, forwardRef, useImperativeHandle, useLayoutEffect } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { uniqBy } from 'lodash'
import DOMPurify from 'dompurify'
import { useNavigate } from 'react-router'

// evertel
import { Button, Col, Row, Text, useUI, Media } from '@evertel/web/ui'
import { CreateMessageToolbar } from './CreateMessageToolbar'
import { convertToText, positionElementRelativeToTarget } from '@evertel/utils'
import { useService } from '@evertel/di'
import { MessageWallContext, CreateMessageController } from '@evertel/message'
import { isExtensionBanned, getFileExt, fileToUploadableMedia } from '@evertel/utils'
import { MediaUploadWindowOverlay } from './MediaUploadWindowOverlay'
import { UploadableMedia } from '@evertel/media'
import { APIDataRoom } from '@evertel/types'
import { MessageDraftStore } from '@evertel/stores'

const domPurifyConfig = {
    USE_PROFILES: { html: true },
    FORBID_ATTR: ['style'],
    FORBID_TAGS: ['style']
}

const sanitize = (content) => {
    return DOMPurify.sanitize(content, domPurifyConfig)
}

interface CreateMessageRef {
    focus: () => void
}

const CreateMessage = observer(forwardRef<CreateMessageRef>((props, ref) => {
    const { setEmojiPickerState, modelType, modelId, allowedToPost, modelData, repliesToId } = useContext(MessageWallContext)
    const createMessageController = useService(CreateMessageController, [])
    const messageDraftStore = useService(MessageDraftStore, [])
    const messageInput = useRef<HTMLDivElement>(null)
    const { addToast, isConfirmed } = useUI()

    const [message, setMessage] = useState('')
    const [media, setMedia] = useState<UploadableMedia[]>([])
    const [caretOffset, setCaretOffset] = useState(1)
    const [isUrgent, setIsUrgent] = useState(false)
    const [submitBttnEnabled, setSubmitBttnEnabled] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const navigate = useNavigate()

    const thumbWidth = 50

    const isArchived = (modelData as APIDataRoom)?.isArchived === true
    const canPost = allowedToPost && !isArchived

    // RoomGate remounts this component on a room switch, but it is reused in
    // place when only repliesToId changes, so the draft has to be (re)loaded
    // here rather than from useState initializers.
    useLayoutEffect(() => {
        createMessageController.init(modelId, modelType, repliesToId)

        // canPost is a dependency because the composer isn't rendered when it's
        // false: without it, a draft restored while posting is disabled would
        // set `message` but never reach the input element.
        if (!canPost) return

        const draft = messageDraftStore.getDraft({ modelType, modelId, repliesToId })
        const text = draft?.text ?? ''

        setMessage(text)
        setIsUrgent(draft?.isUrgent ?? false)

        // Attachments are never drafted, and must not follow the user into
        // another conversation when this component is reused in place.
        setMedia([])

        // Emoji insertion splices at this offset, so park it at the end of the
        // restored text instead of leaving it at its initial value.
        setCaretOffset(text.length)

        if (messageInput.current) {
            // textContent, not innerHTML: drafts hold sanitized plain text, and
            // .message-input is white-space: pre-wrap so newlines still render.
            messageInput.current.textContent = text

            // Typing should continue where the draft left off. Skipped when
            // there is no draft so an empty composer doesn't grab the selection.
            if (text) moveCaretToEnd(messageInput.current)
        }
    }, [
        createMessageController,
        messageDraftStore,
        modelType,
        modelId,
        repliesToId,
        canPost
    ])

    useEffect(() => {
        // enable/disable 'send' button
        if (message.length > 0 || media?.length) {
            setSubmitBttnEnabled(true)
        } else {
            setSubmitBttnEnabled(false)
        }
    }, [message, media])

    useImperativeHandle(ref, () => ({
        focus: () => {
            messageInput.current?.focus()
            // MessageWall focuses the composer on a room switch, which happens
            // after the draft above is written to the DOM. focus() alone would
            // put the caret in front of the restored text.
            moveCaretToEnd(messageInput.current)
        }
    }))

    const persistDraft = (patch: { text?: string, isUrgent?: boolean }) => {
        messageDraftStore.setDraft({ modelType, modelId, repliesToId }, {
            text: message,
            isUrgent,
            ...patch
        })
    }

    //=========================================
    // TOOLBAR CALLBACKS
    //=========================================

    const onClickToolbarOption = (option: string) => {
        switch (option) {
            case 'emoji-picker':
                openEmojiPicker()
                break
            case 'urgent':
                setIsUrgent(!isUrgent)
                persistDraft({ isUrgent: !isUrgent })
                break
            case 'attach':
                onClickAttachMedia()
                break
            default:
                break
        }
    }

    //=========================================
    // FILE DRAG OVERLAY DROP HANDLER
    //=========================================

    const onDrop = async (files: File[]) => {
        // convert file to uploadable media
        const uploadableMediaFile = await Promise.all(files.map(file => fileToUploadableMedia(file)))
        setMedia(uniqBy([...media, ...uploadableMediaFile], 'fileName'))
    }

    //=========================================
    // EMOJIS
    //=========================================

    const onSelectEmoji = (emoji: any) => {
        setEmojiPickerState({
            visible: false,
            params: {}
        })

        const input = messageInput.current
        input.innerHTML = input.innerHTML.substr(0, caretOffset) + emoji.text + input.innerHTML.substr(caretOffset, input.innerHTML.length)

        setMessage(input.innerText)
        setCaretOffset(caretOffset + (emoji.text?.length ?? 0))
        persistDraft({ text: input.innerText })
    }

    const openEmojiPicker = () => {
        positionElementRelativeToTarget('#emoji-picker', '#message-input')
        setEmojiPickerState({
            visible: true,
            params: {
                onSelectEmoji: onSelectEmoji
            }
        })
    }

    //=========================================
    // MESSAGE INPUT
    //=========================================

    const onMessageChange = (e) => {
        const content = e.target.innerText?.trim() || ''
        const sanitizedContent = sanitize(content)
        getCaretPosition(e)
        setMessage(sanitizedContent)
        if (messageInput.current.innerHTML === '<br>') {
            // fixes missing placeholder
            messageInput.current.innerHTML = ''
        }
        persistDraft({ text: sanitizedContent })
    }

    const onMessageInputKeyDown = (e) => {
        // enter key submits message (shift + enter adds line break)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            // Prevent multiple submissions if already loading
            if (!isLoading) {
                onSubmit()
            }
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            // attempt to shift focus to message list
            const messageInput = document.getElementById('virtuoso-scroller')
            messageInput?.focus()
        }
    }

    const onPaste = (e) => {
        e.preventDefault()

        const items = e.clipboardData?.items
        const imageFiles = []

        for (let i = 0; i < items.length; i++) {
            const item = items[i]
            if (item.type.indexOf('image') !== -1) {
                const file = item.getAsFile()
                imageFiles.push(file)
            }
        }

        if (imageFiles.length > 0) {
            onDrop(imageFiles)
        } else {
            const textPlain = e.clipboardData.getData('text/plain')
            insertContentAtCaret(sanitize(textPlain))
            getCaretPosition(e)
        }
    }

    const insertContentAtCaret = (content) => {
        const selection = window.getSelection()

        if (selection.rangeCount) {
            // Focus the editable element
            if (selection.focusNode && selection.focusNode.parentElement) {
                selection.focusNode.parentElement.focus()
            }
            // Use execCommand to insert content
            // NOTE: execCommand is deprecated, but it's the only way to support CTRL-Z and it's still widely supported
            document.execCommand('insertHTML', false, content)
        }
    }

    const getCaretPosition = (e) => {
        const selection = window.getSelection()
        if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0)
            const preCaretRange = range.cloneRange()
            preCaretRange.selectNodeContents(e.target)
            preCaretRange.setEnd(range.endContainer, range.endOffset)
            const caretOffset = preCaretRange.toString().length
            setCaretOffset(caretOffset)
        }
    }

    //=========================================
    // MEDIA
    //=========================================

    const onClickAttachMedia = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.multiple = true
        input.onchange = async _ => {
            const files = Array.from(input.files)
            // convert file to uploadable media

            const rejectedExtensions = new Set()
            const uploadableMediaFiles = (await Promise.all(
                files.map(async (file) => {
                    const ext = getFileExt(file)
                    if (isExtensionBanned(ext)) {
                        rejectedExtensions.add(ext)
                        return null
                    } else {
                        return await fileToUploadableMedia(file)
                    }
                })
            )).filter(Boolean) //remove null items

            if (rejectedExtensions.size) {
                addToast({
                    color: 'danger',
                    message: `Unsupported file format found: ${[...rejectedExtensions].join(', ')}`
                })
            }

            setMedia(uniqBy([...media, ...uploadableMediaFiles], 'fileName'))
        }
        input.click()
    }

    const onRemoveMedia = (remove: UploadableMedia) => {
        setMedia(media.filter(m => m.id !== remove.id))
    }

    //=========================================
    // SUBMIT HANDLER
    //=========================================

    const onSubmit = async () => {
        // there must be some text or at least one attachment to submit
        if (!media?.length && message?.trim() === '') {
            return
        }

        // If Room is archived, dont do anything
        if (isArchived) return

        if (isUrgent) {
            const confirmed = await isConfirmed({
                title: 'Send as Urgent?',
                message: 'Everyone in this room will be notified immediately, even if they\'ve silenced notifications.',
                icon: {
                    name: 'triangle-exclamation',
                    color: 'white',
                    size: 24,
                    className: 'd-inline-flex justify-content-center align-items-center rounded-circle bg-danger me-2',
                    style: { width: 40, height: 40 }
                },
                acceptButton: { label: 'Send Urgent', color: 'primary' }
            })
            if (!confirmed) return
        }

        setIsLoading(true)

        try {
            const sanitizedMessage = sanitize(message)

            await createMessageController.postMessage(convertToText(sanitizedMessage), isUrgent, media)

            // reset everything
            setIsUrgent(false)
            setMessage('')
            setMedia([])
            messageInput.current.innerHTML = ''

        } catch (error) {
            console.error(error.message)

            switch (error.code) {
                case 'AUTHORIZATION_REQUIRED':
                    addToast({
                        color: 'danger',
                        message: `You don't belong to this ${createMessageController.modelType} anymore`
                    })
                    navigate('/landing')
                    break
                case 'ROOM_IS_ARCHIVED':
                    addToast({
                        color: 'warning',
                        message: `This rooms is archived`
                    })
                    break
                default:
                    addToast({
                        color: 'warning',
                        message: error?.message
                    })
                    break
            }

        } finally {
            messageInput.current?.focus()
        }

        setIsLoading(false)
    }

    if (!canPost) {
        // not allowed to post
        return (
            <Col className="create-message-box">
                <Text
                    italic
                    className="p-4"
                >
                    {`Posting is disabled in this ${modelType === 'room' ? 'room' : 'conversation'}`}
                </Text>
            </Col>
        )
    }
    // allowed to post
    return (
        <Col className="create-message-box">
            <MediaUploadWindowOverlay onDrop={onDrop} />
            <div className="message-input-container">
                <div
                    id="message-input"
                    className={classNames('form-control message-input', { urgent: isUrgent })}
                    ref={messageInput}
                    contentEditable="true"
                    role="textbox"
                    onKeyUp={onMessageChange}
                    onKeyDown={onMessageInputKeyDown}
                    onBlur={onMessageChange}
                    onPaste={onPaste}
                    tabIndex={0}
                    data-ph={`Type your ${repliesToId ? 'reply' : 'message'} here...`}
                    aria-label={`Type your ${repliesToId ? 'reply' : 'message'} here...`}
                    aria-multiline="true"
                />
                {(!!media?.length) && (
                    <Row className="attachments-container">
                        {media?.map(m => (
                            <Media
                                key={m.id}
                                media={m}
                                variant="file-attachment"
                                width={thumbWidth}
                                height={thumbWidth}
                                fileProps={{
                                    width: 200
                                }}
                                videoProps={{ asThumbnail: true }}
                                imageProps={{ flexibility: 'fixed' }}
                                resizeMode="cover"
                                rounded
                                onDelete={onRemoveMedia}
                                className=""
                            />
                        ))}
                    </Row>
                )}
            </div>
            <Row align="between">
                <CreateMessageToolbar
                    isUrgent={isUrgent}
                    onClickOption={onClickToolbarOption}
                />
                <div>
                    <Button
                        outline={!submitBttnEnabled}
                        color={(submitBttnEnabled) ? 'primary' : 'muted'}
                        onClick={onSubmit}
                        disabled={isLoading}
                        className="py-1 px-2 my-1">
                        Send
                    </Button>
                </div>
            </Row>
        </Col>
    )
}))

CreateMessage.displayName = 'CreateMessage'

// Placing the caret is a DOM-level concern that the `caretOffset` state does not
// cover (that one only feeds the emoji splice). Without this, focusing a
// contenteditable that was populated programmatically drops the caret at offset 0.
const moveCaretToEnd = (el: HTMLElement | null) => {
    const selection = window.getSelection()

    if (!el || !selection) return

    const range = document.createRange()
    range.selectNodeContents(el)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
}

export { CreateMessage }
